import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit, Trash2, Building2 } from "lucide-react";
import { hospitalService, Hospital } from "../../services/hospital.service";
import { showToast } from "../../lib/toast";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";

export function ViewHospitalPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [hospital, setHospital] = useState<Hospital | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (id) {
      loadHospital();
    }
  }, [id]);

  const loadHospital = async () => {
    try {
      setLoading(true);
      const data = await hospitalService.getById(id!);
      setHospital(data);
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to load hospital");
      navigate("/hospitals");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await hospitalService.delete(id!);
      showToast.success("Hospital deleted successfully");
      navigate("/hospitals");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to delete hospital",
      );
    } finally {
      setIsDeleting(false);
      setDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Loading...</div>
      </div>
    );
  }

  if (!hospital) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Hospital not found</div>
      </div>
    );
  }

  const getFacilityTypeColor = (type: string) => {
    switch (type) {
      case "HOSPITAL":
        return "bg-blue-100 text-blue-800";
      case "CLINIC":
        return "bg-green-100 text-green-800";
      case "NURSING_HOME":
        return "bg-purple-100 text-purple-800";
      case "DIAGNOSTIC_CENTER":
        return "bg-orange-100 text-orange-800";
      case "PHC":
      case "CHC":
        return "bg-teal-100 text-teal-800";
      case "PHARMACY":
        return "bg-pink-100 text-pink-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatFacilityType = (type: string) => {
    return type.replace(/_/g, " ");
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/hospitals")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Hospitals
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {hospital.hospitalName}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Code: {hospital.hospitalCode}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate(`/hospitals/${id}/edit`)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Edit className="h-4 w-4" />
              Edit
            </button>
            <button
              onClick={() => setDeleteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Hospital Code</span>
              <span className="font-medium font-mono">
                {hospital.hospitalCode}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Hospital Name</span>
              <span className="font-medium">{hospital.hospitalName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-500">Facility Type</span>
              <span
                className={`px-2 py-1 text-xs rounded-full ${getFacilityTypeColor(hospital.facilityType)}`}
              >
                {formatFacilityType(hospital.facilityType)}
              </span>
            </div>
            {hospital.registrationNumber && (
              <div className="flex justify-between">
                <span className="text-gray-500">Registration Number</span>
                <span className="font-medium">
                  {hospital.registrationNumber}
                </span>
              </div>
            )}
            {hospital.bedCapacity && (
              <div className="flex justify-between">
                <span className="text-gray-500">Bed Capacity</span>
                <span className="font-medium">{hospital.bedCapacity}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-gray-500">Status</span>
              <span
                className={`px-2 py-1 text-xs rounded-full ${
                  hospital.isActive
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {hospital.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Phone</span>
              <span className="font-medium">{hospital.phone || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Email</span>
              <span className="font-medium">{hospital.email || "-"}</span>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Address</h2>
          <div className="space-y-3">
            {hospital.address && (
              <div className="flex justify-between">
                <span className="text-gray-500">Address</span>
                <span className="font-medium text-right max-w-xs">
                  {hospital.address}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500">City</span>
              <span className="font-medium">{hospital.city || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">State</span>
              <span className="font-medium">{hospital.state || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Pincode</span>
              <span className="font-medium">{hospital.pincode || "-"}</span>
            </div>
          </div>
        </div>

        {/* Statistics */}
        {hospital._count && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Statistics</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Doctors</span>
                <span className="font-medium">
                  {hospital._count.doctors || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Invoices</span>
                <span className="font-medium">
                  {hospital._count.invoices || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Prescriptions</span>
                <span className="font-medium">
                  {hospital._count.prescriptions || 0}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Hospital"
        message={`Are you sure you want to delete "${hospital.hospitalName}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={isDeleting}
        variant="danger"
      />
    </div>
  );
}

export default ViewHospitalPage;
